import { HandCoins, X } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { errorMessage } from '../../lib/api'
import { formatLeaveDays } from '../../lib/leaveDays'
import {
  canDecideSettlement,
  myPayoutPayLine,
  payoutBadgeText,
  payoutsAwaitingMe,
  payoutSummary,
  REQUESTABLE_TRIGGERS,
  settlementFormValid,
  settlementStageLabel,
  settlementStageRows,
  settlementStatusLabel,
  settlementTriggerLabel,
  useCreateSettlement,
  useDecideSettlement,
  usePayoutPreview,
  useSettlements,
  type Settlement,
} from '../../lib/leaveSettlement'
import { formatMoney } from '../../lib/money'
import { useMe } from '../../lib/queries'
import { HelpTip } from '../../components/ui/HelpTip'
import { Badge, Button, IconButton, labelClass, TextArea, TextInput } from '../../components/ui'
import { Overlay } from './LeaveDialogs'

export function LeavePayoutDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Overlay labelledBy="leave-payout-title" onClose={onClose} wide>
      <div className="flex items-start justify-between gap-3">
        <h2 id="leave-payout-title" className="text-base font-semibold text-slate-800 dark:text-slate-100">
          {t('me.settlementButton')}
        </h2>
        <IconButton label={t('action.cancel')} icon={<X className="h-4 w-4" />} onClick={onClose} />
      </div>
      <LeavePayoutRequest framed={false} />
    </Overlay>
  )
}

/**
 * Raise a leave pay-out and show the caller's own in flight. `useSettlements()` returns the full queue for signers,
 * so it is filtered to `person_id === me.id`.
 */
export default function LeavePayoutRequest({ framed = true }: { framed?: boolean }) {
  const { t, i18n } = useTranslation()
  const { data: me } = useMe()
  const { data: settlements } = useSettlements()
  const create = useCreateSettlement()

  const trigger = REQUESTABLE_TRIGGERS[0]
  const [requestedDays, setRequestedDays] = useState('')
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)

  const preview = usePayoutPreview(requestedDays)
  const summary = payoutSummary(preview.data, t, i18n.language)
  const daysMissing = !(Number(requestedDays) > 0)

  const mine: Settlement[] = (settlements ?? []).filter((s) => s.person_id === me?.id)

  function submit() {
    if (!settlementFormValid(trigger, '', requestedDays)) {
      setTried(true)
      return
    }
    create
      .mutateAsync({
        trigger_type: trigger,
        relevant_date: null,
        requested_days: Number(requestedDays),
        reason,
      })
      .then(() => {
        toast.success(t('payroll.settlementRequested'))
        setRequestedDays('')
        setReason('')
        setTried(false)
      })
      .catch((err) => toast.error(`${t('me.settlementFailed')} ${errorMessage(err)}`))
  }

  return (
    <section
      className={
        framed
          ? 'space-y-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800'
          : 'space-y-3'
      }
    >
      <div className="flex items-center gap-1">
        {framed && (
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('me.settlementButton')}</h2>
        )}
        <HelpTip label={t('me.settlementButton')}>{t('payroll.settlementRequestHint')}</HelpTip>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <TextInput
          label={t('payroll.settlementDays')}
          required
          type="number"
          min="0.5"
          step="0.5"
          value={requestedDays}
          onChange={(e) => setRequestedDays(e.target.value)}
          error={tried && daysMissing ? t('validation.required') : undefined}
        />

        <TextArea
          label={t('payroll.settlementReason')}
          className="sm:col-span-2 lg:col-span-3"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={2000}
        />
      </div>

      {(summary.balance || summary.problem) && (
        <div
          aria-live="polite"
          className="space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900/40"
        >
          {summary.balance && (
            <p className="font-medium tabular-nums text-slate-800 dark:text-slate-100">{summary.balance}</p>
          )}
          {summary.amount && <p className="text-slate-700 dark:text-slate-200">{summary.amount}</p>}
          {summary.payDay && <p className="text-slate-700 dark:text-slate-200">{summary.payDay}</p>}
          {summary.pending && <p className="text-slate-500 dark:text-slate-400">{summary.pending}</p>}
          {summary.problem && <p className="text-amber-800 dark:text-amber-300">{summary.problem}</p>}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={submit}
          loading={create.isPending}
          disabled={!!summary.problem}
          disabledReason={summary.problem ?? undefined}
        >
          {t('payroll.settlementSubmit')}
        </Button>
      </div>

      {mine.length > 0 && <MyLeavePayouts rows={mine} />}
    </section>
  )
}

/** The caller's own requests, read-only, for a page that no longer raises them; nothing when there are none. */
export function MyLeavePayoutHistory() {
  const { data: me } = useMe()
  const { data: settlements } = useSettlements()
  const mine = (settlements ?? []).filter((s) => s.person_id === me?.id)
  if (mine.length === 0) return null
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <MyLeavePayouts rows={mine} bare />
    </section>
  )
}

/** The caller's own requests, past ones included, so a Resignation raised before the close-out took over still lists. */
function MyLeavePayouts({ rows, bare = false }: { rows: Settlement[]; bare?: boolean }) {
  const { t, i18n } = useTranslation()
  return (
    <div className={bare ? '' : 'border-t border-slate-200 pt-3 dark:border-slate-700'}>
      <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {t('payroll.settlementMyRequests')}
      </h3>
      <ul className="mt-2 space-y-1 text-sm">
        {rows.map((s) => {
          const pay = myPayoutPayLine(s, t)
          return (
            <li key={s.id} className="flex flex-wrap justify-between gap-x-3">
              <span>
                {settlementTriggerLabel(s.trigger_type, t)} · {formatLeaveDays(s.requested_days, i18n.language)}{' '}
                {t('payroll.settlementDaysUnit')}
              </span>
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                {settlementStatusLabel(s.status, t)}
                {pay && <> · {pay}</>}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** The pay-outs the caller signs a stage of, as a button that is absent while there are none. */
function PayoutRow({ settlement, name, onOpen }: { settlement: Settlement; name: string; onOpen: () => void }) {
  const { t, i18n } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 p-3 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700/40"
    >
      <Badge tone="info" icon={<HandCoins aria-hidden="true" className="h-3 w-3" />}>
        {t('payroll.payoutTag')}
      </Badge>
      <span className="min-w-0 flex-1 truncate font-medium text-blue-700 dark:text-blue-400">{name}</span>
      <span className="shrink-0 whitespace-nowrap tabular-nums text-xs text-slate-500 dark:text-slate-400">
        {formatLeaveDays(settlement.requested_days, i18n.language)} {t('payroll.settlementDaysUnit')}
      </span>
    </button>
  )
}

function useDecidePopup(nameOf: (personId: string) => string) {
  const { data: me } = useMe()
  const { data: settlements } = useSettlements()
  const [openId, setOpenId] = useState<string | null>(null)
  const open = (settlements ?? []).find((s) => s.id === openId) ?? null
  const dialog = open && (
    <SettlementDecideDialog
      key={`${open.id}-${open.status}`}
      settlement={open}
      personName={nameOf(open.person_id)}
      meId={me?.id}
      onClose={() => setOpenId(null)}
    />
  )
  return { meId: me?.id, settlements, setOpenId, dialog }
}

/** Pay-outs awaiting the caller, as tagged rows at the head of the leave applications awaiting them. */
export function PayoutsAwaitingRows({
  nameOf,
  personId = null,
}: {
  nameOf: (personId: string) => string
  personId?: string | null
}) {
  const { t } = useTranslation()
  const { meId, settlements, setOpenId, dialog } = useDecidePopup(nameOf)
  const rows = payoutsAwaitingMe(settlements, meId, personId)
  if (rows.length === 0 && !dialog) return null
  return (
    <>
      {rows.length > 0 && (
        <ul
          aria-label={t('payroll.settlementsToDecide')}
          className="shrink-0 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white dark:divide-slate-700/60 dark:border-slate-700 dark:bg-slate-800"
        >
          {rows.map((s) => (
            <li key={s.id}>
              <PayoutRow settlement={s} name={nameOf(s.person_id)} onOpen={() => setOpenId(s.id)} />
            </li>
          ))}
        </ul>
      )}
      {dialog}
    </>
  )
}

/** The pay-out icon with its count; absent at zero, it opens the list and each row the decide popup. */
export function PayoutsAwaitingBadge({ nameOf }: { nameOf: (personId: string) => string }) {
  const { t } = useTranslation()
  const titleId = useId()
  const { meId, settlements, setOpenId, dialog } = useDecidePopup(nameOf)
  const [listOpen, setListOpen] = useState(false)
  const rows = payoutsAwaitingMe(settlements, meId)
  const text = payoutBadgeText(rows.length)
  if (text === null && !dialog) return null
  const label = t('payroll.payoutsAwaitingBadge', { count: rows.length })
  return (
    <>
      {text !== null && (
        <button
          type="button"
          title={label}
          aria-label={label}
          onClick={() => setListOpen(true)}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/40"
        >
          <HandCoins aria-hidden="true" className="h-4 w-4" />
          <span
            aria-hidden="true"
            className="absolute -right-1.5 -top-1.5 min-w-[1.25rem] rounded-full bg-amber-700 px-1 text-center text-[11px] font-semibold leading-5 text-white tabular-nums dark:bg-amber-500 dark:text-slate-900"
          >
            {text}
          </span>
        </button>
      )}
      {listOpen && (
        <Overlay labelledBy={titleId} onClose={() => setListOpen(false)}>
          <div className="flex items-start justify-between gap-3">
            <h2 id={titleId} className="text-base font-semibold text-slate-800 dark:text-slate-100">
              {t('payroll.settlementsToDecide')}
            </h2>
            <IconButton label={t('action.cancel')} icon={<X className="h-4 w-4" />} onClick={() => setListOpen(false)} />
          </div>
          <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.map((s) => (
              <li key={s.id}>
                <PayoutRow
                  settlement={s}
                  name={nameOf(s.person_id)}
                  onOpen={() => {
                    setListOpen(false)
                    setOpenId(s.id)
                  }}
                />
              </li>
            ))}
          </ul>
        </Overlay>
      )}
      {dialog}
    </>
  )
}

export function SettlementDecideDialog({
  settlement,
  personName,
  meId,
  onClose,
}: {
  settlement: Settlement
  personName: string
  meId: string | undefined
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const titleId = useId()
  const decide = useDecideSettlement()
  const [comment, setComment] = useState('')
  const mayDecide = canDecideSettlement(settlement, meId)

  function act(verdict: 'Approved' | 'Rejected') {
    decide
      .mutateAsync({ settlement_id: settlement.id, verdict, comment })
      .then(() => {
        toast.success(t('payroll.settlementDecided'))
        onClose()
      })
      .catch((err) => toast.error(errorMessage(err)))
  }

  const lines: { label: string; value: string }[] = [
    {
      label: t('payroll.settlementDays'),
      value: formatLeaveDays(settlement.requested_days, i18n.language),
    },
    ...(settlement.relevant_date ? [{ label: t('payroll.settlementRelevantDate'), value: settlement.relevant_date }] : []),
    ...(settlement.amount != null ? [{ label: t('payroll.settlementAmount'), value: formatMoney(settlement.amount) }] : []),
    ...settlementStageRows(settlement).map(({ stage, approval }) => ({
      label: settlementStageLabel(stage, t),
      value: settlementStatusLabel(approval, t),
    })),
  ]

  return (
    <Overlay labelledBy={titleId} onClose={onClose}>
      <div>
        <h2 id={titleId} className="text-base font-semibold text-slate-800 dark:text-slate-100">
          {personName}
        </h2>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {settlementTriggerLabel(settlement.trigger_type, t)} · {settlementStatusLabel(settlement.status, t)}
        </p>
      </div>

      <dl className="grid gap-y-1 rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700">
        {lines.map((line) => (
          <div key={line.label} className="flex justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">{line.label}</dt>
            <dd className="tabular-nums text-slate-700 dark:text-slate-200">{line.value}</dd>
          </div>
        ))}
      </dl>

      {settlement.reason && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className={labelClass}>{t('payroll.settlementReason')}</span>
          {settlement.reason}
        </p>
      )}
      {settlement.comment && (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className={labelClass}>{t('payroll.settlementComment')}</span>
          {settlement.comment}
        </p>
      )}

      {mayDecide && (
        <>
          <TextArea
            label={t('payroll.settlementComment')}
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('payroll.settlementRejectionHint')}
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('payroll.settlementYouSign', {
              stages: settlement.my_stages.map((stage) => settlementStageLabel(stage, t)).join(', '),
            })}
          </p>
        </>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {mayDecide && (
          <>
            {/* The server 422s a reasonless rejection; disabling here turns that into a visible reason. */}
            <Button
              variant="danger"
              onClick={() => act('Rejected')}
              disabled={!comment.trim() || decide.isPending}
              disabledReason={comment.trim() ? undefined : t('payroll.settlementCommentRequired')}
            >
              {t('payroll.settlementReject')}
            </Button>
            <Button
              variant="approve"
              onClick={() => act('Approved')}
              disabled={decide.isPending}
              disabledReason={t('payroll.busyWhy')}
            >
              {t('payroll.settlementApprove')}
            </Button>
          </>
        )}
        <Button onClick={onClose}>{t('payroll.settlementClose')}</Button>
      </div>
    </Overlay>
  )
}
